import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Aggregations } from 'js/model/rainbow/browse-page/Aggregations';
import { ChannelOutput } from 'js/model/rainbow/content/ChannelOutput';
import { CmsBrowsePage, CmsFilters } from 'js/model/cms/cms-browse-page';
import { formatPrice } from 'js/helpers/price/priceUtilities';
import { SearchOptions } from 'js/model/rainbow/browse-page/SearchOptions';
import { VenueListingSpecificationOutput } from 'js/model/rainbow/browse-page/VenueListingSpecificationOutput';
import { VenueListingCriteria } from 'js/model/rainbow/browse-page/VenueListingCriteria';
import { Context } from 'js/components/LocaleWrapper';
import { CmsVenuePage } from 'js/model/cms/cms-venue-page';
import {
  AddLocation,
  browseRemovalParameterType,
  changeBrowseDataContext,
  ChangeBrowseDataFunction,
} from '../BrowseData';
import { RadioOption } from '../FilterModal/RadioGroupFilter';
import { ALL_BRANDS } from '../FilterModal/BrandFilter/BrandFilter';
import {
  PRICE_RANGE_FILTER_EXPONENT,
  findClosest,
  getPriceRangeLowerBound,
  getPriceRangeUpperBound,
  isPriceRangeFilterActive,
  isPriceRangeFilterAvailable,
} from '../Filters/price-range-filter';
import { createNonLinearSteps } from '../Filters/nonLinearSteps';
import {
  getTopRatedAccoladeId,
  isTopRatedSalonSelected,
} from '../Filters/salons-filter';
import { buildSortOptions, isSortFilterActive } from '../Filters/sort-filter';
import {
  EXPRESS_TREATMENT_SERVICE_DURATION,
  isExpressTreatmentTypeSelected,
} from '../Filters/treatment-types-filter';
import { isRatingFilterActive } from './RatingFilter/RatingFilter';
import { getAmenityIds, getProductBrandIds } from './Filters';

export type FilterType =
  | 'sortBy'
  | 'rating'
  | 'price'
  | 'smartDiscounts'
  | 'expressTreatments'
  | 'amenities'
  | 'brands'
  | 'topRatedVenues'
  | 'newVenues';

export type FilterWithOptionsType = Extract<
  FilterType,
  'sortBy' | 'rating' | 'price' | 'amenities' | 'brands'
>;

export type FilterWithoutOptionsType = Exclude<
  FilterType,
  FilterWithOptionsType
>;

type BaseFilter<T extends FilterType> = {
  id: T;
  chipLabel: string;
  isActive: boolean;
};

type FilterWithOptions<T extends FilterWithOptionsType> = BaseFilter<T> & {
  hasOptions: true;
  title: string;
  count?: number;
};

type FilterWithoutOptions<T extends FilterWithoutOptionsType> =
  BaseFilter<T> & {
    hasOptions: false;
  };

export type Filter<T extends FilterType> = T extends FilterWithOptionsType
  ? FilterWithOptions<T>
  : T extends FilterWithoutOptionsType
    ? FilterWithoutOptions<T>
    : never;

const PRICE_RANGE_FILTER_STEPS = 20;
const EN_DASH = '\u2013';

const getSelectedOrDefaultLabel = (
  selectedOptions: string[],
  defaultLabel: string
): string => {
  if (selectedOptions.length === 1) {
    return selectedOptions[0];
  }

  return defaultLabel;
};

const buildParametersToRemove = (
  criteria: VenueListingCriteria
): browseRemovalParameterType[] => {
  const parametersToRemove: browseRemovalParameterType[] = [
    browseRemovalParameterType.MAXDURATION,
    browseRemovalParameterType.ACCOLADE,
    browseRemovalParameterType.BOOKINGTYPE,
  ];

  if (!criteria.price) {
    parametersToRemove.push(browseRemovalParameterType.PRICERANGEFROM);
    parametersToRemove.push(browseRemovalParameterType.PRICERANGETO);
  }

  if (!criteria.amenityIds) {
    parametersToRemove.push(browseRemovalParameterType.AMENITIES);
  }

  if (!criteria.productBrandIds) {
    parametersToRemove.push(browseRemovalParameterType.BRANDS);
  }

  if (!criteria.minReviewRating) {
    parametersToRemove.push(browseRemovalParameterType.MIN_REVIEW_RATING);
  }

  return parametersToRemove;
};

const clearAllFilters = (changeBrowseData: ChangeBrowseDataFunction) => {
  const criteria: VenueListingCriteria = {
    sort: 'recommended',
    newVenues: false,
    yieldDiscounted: false,
  };

  changeBrowseData(
    criteria,
    buildParametersToRemove(criteria),
    AddLocation.CurrentPageWithRadius
  );
};

const applyFilters = (
  changeBrowseData: ChangeBrowseDataFunction,
  criteria: VenueListingCriteria
) => {
  changeBrowseData(
    criteria,
    buildParametersToRemove(criteria),
    AddLocation.CurrentPageWithRadius
  );
};

const getSortFilterLabel = (
  { sort }: VenueListingSpecificationOutput,
  sortOptions: RadioOption<SearchOptions['sortOptions'][number]>[],
  cmsFilters: CmsFilters
) => {
  const sortOption = sortOptions.find(
    (sortOption) => sortOption.value === sort
  );
  const label = sortOption?.label ?? cmsFilters['sort-options'].recommended;

  return sortOption?.value === 'recommended'
    ? cmsFilters['category-label-sort']
    : label;
};

const getPriceRangeFilterLabel = (
  searchOptions: SearchOptions,
  specification: VenueListingSpecificationOutput,
  cmsFilters: CmsFilters,
  channel: ChannelOutput
) => {
  if (isPriceRangeFilterActive(searchOptions, specification)) {
    const availableSteps = createNonLinearSteps(
      searchOptions.minServicePrice!,
      searchOptions.maxServicePrice!,
      PRICE_RANGE_FILTER_STEPS,
      PRICE_RANGE_FILTER_EXPONENT
    );
    const lowerBound = findClosest(
      availableSteps,
      getPriceRangeLowerBound(searchOptions, specification)
    );
    const upperBound = findClosest(
      availableSteps,
      getPriceRangeUpperBound(searchOptions, specification)
    );
    const lowerBoundPrice = formatPrice(lowerBound, channel);
    const upperBoundPrice = formatPrice(upperBound, channel);

    return `${lowerBoundPrice} ${EN_DASH} ${upperBoundPrice}`;
  }

  return cmsFilters['any-price'];
};

const getAmenitiesFilterLabel = (
  specification: VenueListingSpecificationOutput,
  title: string
) => {
  const amenities =
    specification.amenities?.map((amenity) => amenity.name) ?? [];

  if (amenities.length === 1) {
    return amenities[0];
  }

  return title;
};

const getBrandsFilterLabel = (
  specification: VenueListingSpecificationOutput,
  title: string
) => {
  const brands = specification.productBrands?.map((brand) => brand.name) ?? [];

  return getSelectedOrDefaultLabel(brands, title);
};

const isTopRatedSalonsFilterAvailable = (
  { accolades }: Aggregations,
  topRatedAccoladeId: number
) => accolades?.some((accolade) => accolade.id === topRatedAccoladeId) ?? false;

export const isFilterWithOptionsGuard = (
  filter: Filter<FilterType>
): filter is FilterWithOptions<FilterWithOptionsType> => filter.hasOptions;

export const getFilterWithOptions = (
  filters: Filter<FilterType>[],
  id: FilterWithOptionsType
): FilterWithOptions<FilterWithOptionsType> | undefined => {
  const filter = filters.find((filter) => filter.id === id);

  if (filter && isFilterWithOptionsGuard(filter)) {
    return filter;
  }
};

const getTopRatedAccolade = (
  { accolades }: Aggregations,
  topRatedAccoladeId: number
) => accolades?.find((accolade) => accolade.id === topRatedAccoladeId);

export const useFilters = (
  aggregations: Aggregations,
  searchOptions: SearchOptions,
  specification: VenueListingSpecificationOutput
): {
  filters: Filter<FilterType>[];
  hasActiveFilters: boolean;
  applyFilters: (
    criteria: VenueListingCriteria,
    removeParameters?: browseRemovalParameterType[],
    addLocation?: AddLocation
  ) => void;
  clearFilters: () => void;
} => {
  const changeBrowseData = useContext(changeBrowseDataContext);
  const { channel, pageData } = useContext(Context);
  const cms = pageData.cms as CmsBrowsePage & CmsVenuePage;
  const cmsFilters = cms.page.browse.filters;
  const cmsPageVenue = cms.page.venue;
  const { t } = useTranslation();
  const sortOptions = buildSortOptions(searchOptions, cmsFilters);
  const availbleAmenities = aggregations.amenities || [];
  const availableBrands = aggregations.brands || [];
  const topRatedAccoladeId = getTopRatedAccoladeId(cmsFilters);
  const appliedFilters: VenueListingCriteria = {
    sort: specification.sort ?? 'recommended',
    minReviewRating: specification.minReviewRating,
    yieldDiscounted: specification.yieldDiscounted ?? false,
    maxDuration: isExpressTreatmentTypeSelected(specification)
      ? EXPRESS_TREATMENT_SERVICE_DURATION
      : undefined,
    amenityIds:
      (specification.amenities ?? []).length > 0
        ? getAmenityIds(specification.amenities ?? [])
        : undefined,
    productBrandIds:
      getProductBrandIds(specification.productBrands ?? [])[0] !== ALL_BRANDS
        ? (getProductBrandIds(specification.productBrands ?? []) as number[])
        : undefined,
    accoladeId: (
      isTopRatedSalonSelected(specification, getTopRatedAccoladeId(cmsFilters))
        ? getTopRatedAccolade(aggregations, getTopRatedAccoladeId(cmsFilters))
        : undefined
    )
      ? getTopRatedAccoladeId(cmsFilters)
      : undefined,
    newVenues: specification.newVenues ?? false,
  };
  const filters: Filter<FilterType>[] = [
    {
      id: 'sortBy',
      chipLabel: getSortFilterLabel(specification, sortOptions, cmsFilters),
      isActive: isSortFilterActive(specification),
      hasOptions: true,
      title: cmsFilters['category-label-sort'],
    },
    {
      id: 'rating',
      chipLabel: specification.minReviewRating
        ? t('browsePage.filter.rating.value', {
            count: specification.minReviewRating,
          })
        : t('browsePage.filter.rating.title'),
      isActive: isRatingFilterActive(specification),
      hasOptions: true,
      title: t('browsePage.filter.rating.title'),
    },
  ];

  if (isPriceRangeFilterAvailable(searchOptions)) {
    if (isPriceRangeFilterActive(searchOptions, specification)) {
      appliedFilters.price = {
        min: getPriceRangeLowerBound(searchOptions, specification),
        max: getPriceRangeUpperBound(searchOptions, specification),
      };
    }

    filters.push({
      id: 'price',
      chipLabel: getPriceRangeFilterLabel(
        searchOptions,
        specification,
        cmsFilters,
        channel
      ),
      isActive: isPriceRangeFilterActive(searchOptions, specification),
      hasOptions: true,
      title: cmsFilters['category-label-price'],
    });
  }

  filters.push(
    {
      id: 'smartDiscounts',
      chipLabel: cmsFilters['smart-discounts'],
      isActive: specification.yieldDiscounted ?? false,
      hasOptions: false,
    },
    {
      id: 'expressTreatments',
      chipLabel: cmsFilters['express-treatments'],
      isActive:
        specification.maxServiceDurationMinutes ===
        EXPRESS_TREATMENT_SERVICE_DURATION,
      hasOptions: false,
    }
  );

  if (availbleAmenities.length > 0) {
    const title = cmsPageVenue.amenities.amenities;
    const selectedAmenities = specification.amenities ?? [];
    const amenitiesCount = selectedAmenities.length;
    const amenitiesFilter: Filter<'amenities'> = {
      id: 'amenities',
      chipLabel: getAmenitiesFilterLabel(specification, title),
      isActive: amenitiesCount > 0,
      hasOptions: true,
      title,
    };

    if (amenitiesCount > 1) {
      amenitiesFilter.count = amenitiesCount;
    }

    filters.push(amenitiesFilter);
  }

  if (availableBrands.length > 0) {
    const title = cmsFilters.brands.brands;
    const selectedBrands = specification.productBrands ?? [];
    const brandsCount = selectedBrands.length;
    const brandsFilter: Filter<'brands'> = {
      id: 'brands',
      chipLabel: getBrandsFilterLabel(specification, title),
      isActive: brandsCount > 0,
      hasOptions: true,
      title,
    };

    if (brandsCount > 1) {
      brandsFilter.count = brandsCount;
    }

    filters.push(brandsFilter);
  }

  if (isTopRatedSalonsFilterAvailable(aggregations, topRatedAccoladeId)) {
    filters.push({
      id: 'topRatedVenues',
      chipLabel: cmsFilters['top-rated-venues'],
      isActive: specification.accolade?.id === topRatedAccoladeId ?? false,
      hasOptions: false,
    });
  }

  filters.push({
    id: 'newVenues',
    chipLabel: t('browsePage.filter.salons.newVenues.title'),
    hasOptions: false,
    isActive: specification.newVenues ?? false,
  });

  return {
    filters,
    hasActiveFilters: filters.some((filter) => filter.isActive),
    applyFilters: (criteria) => {
      applyFilters(changeBrowseData, { ...appliedFilters, ...criteria });
    },
    clearFilters: () => {
      clearAllFilters(changeBrowseData);
    },
  };
};
